import { Component } from '@theme/component';

/**
 * Mapa de lojas parceiras "Go Out": dropdown de estado > abas de categoria >
 * lista de lojas, atualizando o iframe do mapa com a loja ativa. Porta
 * components/GoOut/Map.tsx do repo deco.cx (bolovo-store) — no original é
 * useState (estado/categoria/loja selecionados); aqui vira classe `is-active`
 * chaveada em conexão e em cada clique/troca.
 *
 * @typedef {object} GoOutMapRefs
 * @property {HTMLIFrameElement} mapFrame
 * @property {HTMLSelectElement} stateSelect
 *
 * @extends {Component<GoOutMapRefs>}
 */
export class GoOutMapComponent extends Component {
  requiredRefs = ['mapFrame'];

  connectedCallback() {
    super.connectedCallback();
    this.#populateStateSelect();
    this.#activateState(this.querySelector('.go-out-map__state'));
  }

  #populateStateSelect() {
    const select = this.refs.stateSelect;
    if (!(select instanceof HTMLSelectElement)) return;

    const states = this.querySelectorAll('.go-out-map__state');
    const wrapper = select.closest('.go-out-map__state-select-wrapper');

    if (states.length <= 1) {
      if (wrapper instanceof HTMLElement) wrapper.style.display = 'none';
      return;
    }

    const options = document.createDocumentFragment();
    for (const state of states) {
      if (!(state instanceof HTMLElement)) continue;

      const option = document.createElement('option');
      option.value = state.dataset.stateId ?? '';
      option.textContent = state.dataset.stateName ?? '';
      options.append(option);
    }
    select.replaceChildren(options);
  }

  /** @param {Event} event */
  handleStateChange(event) {
    if (!(event.target instanceof HTMLSelectElement)) return;

    const state = this.querySelector(`.go-out-map__state[data-state-id="${event.target.value}"]`);
    this.#activateState(state);
  }

  /** @param {Event} event */
  handleCategoryClick(event) {
    if (!(event.target instanceof Element)) return;

    const tab = event.target.closest('.go-out-map__category-tab');
    const group = tab?.closest('.go-out-map__category-group');
    const state = tab?.closest('.go-out-map__state');

    if (group instanceof HTMLElement && state instanceof HTMLElement) {
      this.#activateCategory(state, group);
    }
  }

  /** @param {Event} event */
  handleStoreClick(event) {
    if (!(event.target instanceof Element)) return;

    const store = event.target.closest('.go-out-map__store');
    const list = store?.closest('.go-out-map__store-list');

    if (list instanceof HTMLElement && store instanceof HTMLElement) {
      this.#activateStore(list, store);
    }
  }

  /** @param {Element | null} state */
  #activateState(state) {
    if (!(state instanceof HTMLElement)) return;

    for (const el of this.querySelectorAll('.go-out-map__state')) {
      el.classList.toggle('is-active', el === state);
    }

    const firstGroup = state.querySelector('.go-out-map__category-group');
    if (firstGroup instanceof HTMLElement) this.#activateCategory(state, firstGroup);
  }

  /**
   * @param {HTMLElement} state
   * @param {HTMLElement} group
   */
  #activateCategory(state, group) {
    for (const candidate of state.querySelectorAll('.go-out-map__category-group')) {
      const isActive = candidate === group;
      candidate.querySelector('.go-out-map__category-tab')?.classList.toggle('is-active', isActive);
      candidate.querySelector('.go-out-map__store-list')?.classList.toggle('is-active', isActive);
    }

    const list = group.querySelector('.go-out-map__store-list');
    const firstStore = group.querySelector('.go-out-map__store');
    if (list instanceof HTMLElement && firstStore instanceof HTMLElement) {
      this.#activateStore(list, firstStore);
    }
  }

  /**
   * @param {HTMLElement} list
   * @param {HTMLElement} store
   */
  #activateStore(list, store) {
    for (const candidate of list.querySelectorAll('.go-out-map__store')) {
      candidate.classList.toggle('is-active', candidate === store);
    }

    const url = store.dataset.mapUrl;
    if (url) this.refs.mapFrame.src = url;
  }
}

customElements.define('go-out-map-component', GoOutMapComponent);
